"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useState } from "react"
import { AlertTriangle, Loader2 } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuth } from "@/hooks/use-auth"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useAiDisclosureSettingsQuery } from "@/hooks/queries/ai-disclosure-settings"
import {
  useChangeAiDisclosureSettingMutation,
} from "@/hooks/mutations/ai-disclosure-settings"
import {
  AI_DISCLOSURE_CHANNELS,
  AI_DISCLOSURE_OPTOUT_CONFIRMATION,
  type AiDisclosureTemplateChannel,
} from "@/lib/ai-disclosure/settings"
import { AiDisclosureWordingOverrides } from "@/components/organization/ai-disclosure-wording-overrides"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type DisclosureSurface = "organization" | "voice" | "messaging" | "email"

const CHANNELS_BY_SURFACE: Record<DisclosureSurface, readonly AiDisclosureTemplateChannel[]> = {
  organization: AI_DISCLOSURE_CHANNELS,
  voice: [],
  messaging: ["messenger"],
  email: ["email_signature"],
}

export function AiDisclosureSettings({ surface }: { surface: DisclosureSurface }) {

  const tUi = useTranslations("Organization.AiDisclosureSettings")
  const tCommonUi = useTranslations("Common")

  const { can } = usePermissions()
  const { isLoading: authLoading } = useAuth()
  const { data, isLoading, error } = useAiDisclosureSettingsQuery()
  const changeSetting = useChangeAiDisclosureSettingMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const canView = can([TENANTS_PERMISSIONS.VIEW_OWN, TENANTS_PERMISSIONS.EDIT_OWN])
  const canEdit = can(TENANTS_PERMISSIONS.EDIT_OWN)

  if (authLoading || (canView && isLoading)) return (
    <Card aria-busy="true">
      <CardHeader>
        <CardTitle><span key={tUi("customerAIDisclosure")}>{tUi("customerAIDisclosure")}</span></CardTitle>
        <Skeleton className="h-4 w-full max-w-xl" />
        <Skeleton className="h-4 w-2/3" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div role="status" className="flex items-center justify-center gap-2 rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
          <span><span key={tUi("loadingAIDisclosure")}>{tUi("loadingAIDisclosure")}</span></span>
        </div>
        <Skeleton className="h-16 w-full" />
      </CardContent>
    </Card>
  )
  if (!canView) return null
  if (!data) return <p className="text-sm text-destructive"><UiErrorText message={error?.message} fallback={tUi("aIDisclosureSettingsUnavailable")} /></p>

  const setEnabled = async (enabled: boolean) => {
    if (!canEdit) return
    if (!enabled) {
      setConfirmOpen(true)
      return
    }
    await changeSetting.mutateAsync({ enabled, expectedEnabled: data.enabled }).catch(() => {})
  }

  const disable = async () => {
    if (!confirmed) return
    await changeSetting.mutateAsync({
      enabled: false,
      expectedEnabled: data.enabled,
      acceptedConfirmationText: AI_DISCLOSURE_OPTOUT_CONFIRMATION,
    })
    setConfirmOpen(false)
    setConfirmed(false)
  }

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle><span key={tUi("customerAIDisclosure")}>{tUi("customerAIDisclosure")}</span></CardTitle>
        <CardDescription>
          <TranslationText>
            {tUi.rich("helpCustomersUnderstandWhenTheyAreInteractingWith", {
              value0: surface === "voice" || surface === "organization"
                ? tUi("callsAreAlwaysRecordedTheRecordingNoticeIs")
                : "",
              global: chunks => <strong className="font-semibold text-foreground">{chunks}</strong>,
              tag0: chunks => <span className="mt-2 block text-xs">{chunks}</span>,
              tag1: chunks => (
                <a href="https://docs.versafy.ai/workspace/profile-and-organization#prepare-customer-ai-disclosure" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2 hover:text-primary/80">
                  {chunks}
                </a>
              ),
              tag2: chunks => <span key={tUi("readMoreInDocs")}>{chunks}</span>,
            })}
          </TranslationText>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3">
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`announce-ai-${surface}`} className="font-medium"><span key={tUi("announceAIAssistance")}>{tUi("announceAIAssistance")}</span></Label>
            <p className="text-xs text-muted-foreground"><span key={tUi("oneSettingForAllAgentsInYourOrganization")}>{tUi("oneSettingForAllAgentsInYourOrganization")}</span></p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span key={data.enabled ? tUi("on") : tUi("off")} aria-hidden="true" className="text-xs font-medium text-muted-foreground">{data.enabled ? tUi("on") : tUi("off")}</span>
            <Switch
              id={`announce-ai-${surface}`}
              checked={data.enabled}
              disabled={!canEdit || changeSetting.isPending}
              onCheckedChange={setEnabled}
            />
          </div>
        </div>
        {!data.enabled && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300" role="status">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="font-medium"><span key={tUi("aIAnnouncementsAreOffForAllAgents")}>{tUi("aIAnnouncementsAreOffForAllAgents")}</span></p>
              {data.latestChange?.source === "initial_state" && (
                <p className="text-xs"><span key={tUi("offSinceAccountCreation")}>{tUi("offSinceAccountCreation")}</span></p>
              )}
            </div>
          </div>
        )}
        {changeSetting.error && (
          <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {/* The mutation already localizes errors, including interpolated agent names. */}
            <p className="min-w-0 break-words"><span key={changeSetting.error.message}>{changeSetting.error.message}</span></p>
          </div>
        )}
        {surface === "voice" && (
          <p className="text-sm text-muted-foreground">
            <span key={tUi("whenEnabledEachVoiceAgentSFirstMessage")}>{tUi("whenEnabledEachVoiceAgentSFirstMessage")}</span></p>
        )}
        {CHANNELS_BY_SURFACE[surface].length > 0 && (
          <AiDisclosureWordingOverrides
            channels={CHANNELS_BY_SURFACE[surface]}
            templates={data.templates}
            storeName={data.storeName}
            disabled={!canEdit}
          />
        )}
      </CardContent>
      <Dialog open={confirmOpen} onOpenChange={(open) => { setConfirmOpen(open); if (!open) setConfirmed(false) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={tUi("turnOffAIAssistanceDisclosure")}>{tUi("turnOffAIAssistanceDisclosure")}</span></DialogTitle>
            <DialogDescription><span key={tUi("thisChangeAffectsAllAgentsInTheOrganization")}>{tUi("thisChangeAffectsAllAgentsInTheOrganization")}</span></DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 py-2">
            <Checkbox id="confirm-ai-optout" className="mt-1 shrink-0" checked={confirmed} onCheckedChange={value => setConfirmed(value === true)} />
            <Label htmlFor="confirm-ai-optout" className="text-sm leading-relaxed">{AI_DISCLOSURE_OPTOUT_CONFIRMATION}</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button variant="destructive" disabled={!confirmed || changeSetting.isPending} onClick={() => { void disable().catch(() => {}) }}>
              {changeSetting.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <span key={tUi("turnOff")}>{tUi("turnOff")}</span></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  )
}
